import React, { useEffect, useState } from "react";
import { RefreshCcw } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Chicago" }) : "";

const rate = (people, sent) => (sent ? `${Math.round((people / sent) * 100)}%` : "—");

const ClickTable = ({ rows, linkLabel, testId, accent }) => (
    <div className="overflow-x-auto" data-testid={testId}>
        <table className="w-full text-sm table-fixed">
            <thead>
                <tr className="text-[10px] font-stencil tracking-widest text-white/40 text-left">
                    <th className="py-2 pr-4 w-1/2">EMAIL</th>
                    <th className="py-2 pr-4 text-right">SENT</th>
                    <th className="py-2 pr-4 text-right">PEOPLE CLICKED</th>
                    <th className="py-2 pr-4 text-right">TOTAL CLICKS</th>
                    <th className="py-2 text-right">CLICK RATE</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={`${r.campaign}-${r.index}`} className="border-t border-white/10" data-testid={`clicks-row-${r.campaign}-${r.index}`}>
                        <td className="py-2 pr-4">
                            <div className="text-white">{r.label}{r.send_at_utc ? ` · ${fmtDate(r.send_at_utc)}` : ""}</div>
                            <div className="text-[11px] text-white/40 truncate max-w-[340px]">{r.subject}</div>
                        </td>
                        <td className="py-2 pr-4 text-right text-white/70">{r.sent}</td>
                        <td className={`py-2 pr-4 text-right font-display text-lg ${accent}`} data-testid={`clicks-people-${r.campaign}-${r.index}`}>{r.people}</td>
                        <td className="py-2 pr-4 text-right text-white/70">{r.clicks}</td>
                        <td className="py-2 text-right text-white/70">{rate(r.people, r.sent)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
        <p className="text-[11px] text-white/40 mt-2">Counts clicks on the {linkLabel} link in each email.</p>
    </div>
);

export const ClickTrackerPanel = ({ authedApi }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await authedApi.get("/admin/link-clicks");
            setData(r.data);
        } catch (err) {
            toast.error("Couldn't load click stats.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="mb-8 border-2 border-sky-600/60 bg-black p-6" data-testid="click-tracker-panel">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div>
                    <div className="font-stencil text-[10px] tracking-[0.3em] text-sky-400 mb-2">[ CLICK TRACKER ]</div>
                    <h3 className="font-display text-2xl uppercase tracking-tight leading-none">Facebook Group &amp; Journal Clicks</h3>
                </div>
                <div className="flex items-center gap-6">
                    <div className="text-right">
                        <div className="text-[10px] font-stencil tracking-widest text-white/40">FB GROUP · PEOPLE</div>
                        <div className="font-display text-3xl text-sky-400 leading-none mt-1" data-testid="fb-unique-people">{data?.fb_unique_people ?? "—"}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] font-stencil tracking-widest text-white/40">GROUP WELCOMES SENT</div>
                        <div className="font-display text-3xl text-green-500 leading-none mt-1" data-testid="fb-welcome-sent">{data?.fb_welcome_sent ?? "—"}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] font-stencil tracking-widest text-white/40">JOURNAL · PEOPLE</div>
                        <div className="font-display text-3xl text-amber-400 leading-none mt-1" data-testid="journal-unique-people">{data?.journal_unique_people ?? "—"}</div>
                    </div>
                    <button onClick={load} disabled={loading} className="btn-ghost text-xs flex items-center gap-2" data-testid="click-tracker-refresh">
                        <RefreshCcw size={14} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>
            </div>
            {data && (
                <div className="space-y-8">
                    <ClickTable rows={data.holiday} linkLabel="Facebook group" testId="fb-clicks-table" accent="text-sky-400" />
                    <ClickTable rows={data.journal} linkLabel="Journal (Amazon)" testId="journal-clicks-table" accent="text-amber-400" />
                </div>
            )}
        </div>
    );
};
